import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  Grid2,
  IconButton,
  List,
  Tab,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import { ThemeProperties } from "../../Theme/ThemeProperties";
import { getFilterData, getPagesMetaData } from "../../Api/Api";
import CartLoader from "../../Helper/Loaders/CartLoader";
import CODE from "../../Static/Constants/StatusCodes";
import { FilterIconSvg } from "../../Features/Table/Icons";
import { PrimaryFormActionButton, PrimaryTooltip } from "../../Components/StyledComponent";
import PrimaryChip from "../../Components/PrimaryChip";
import SearchInput from "../Overview/components/SearchInput";
import CustomCheckbox from "../Overview/components/Checkbox";

const ItemRowCheckBoxLabel = styled(FormControlLabel)(() => ({
  display: "block",
  marginLeft: 0,
  ".MuiButtonBase-root": { padding: 0 },
  ".MuiCheckbox-root": {
    padding: 0,
    color: "#A2A2A2 !important",
    "&.Mui-checked": { color: "#494949 !important" },
  },
  ".MuiSvgIcon-root": { width: "12px", height: "12px" },
}));

// Generic accordion filter — replicates ProjectFilter/FaultCategoryFilter UX
// but driven by props instead of hardcoded API calls.
function DynamicFilterAccordion({ title, options, searchPlaceholder, selectedValues, onApply }) {
  const [search, setSearch] = useState("");
  const [tempSelected, setTempSelected] = useState(selectedValues);
  const [appliedSelected, setAppliedSelected] = useState(selectedValues);

  // Sync when parent resets
  useEffect(() => {
    setTempSelected(selectedValues);
    setAppliedSelected(selectedValues);
  }, [selectedValues]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    return options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));
  }, [search, options]);

  const handleSelection = (option) => {
    setTempSelected((prev) =>
      prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]
    );
  };

  const handleClearSelection = () => setTempSelected([]);

  const handleApply = () => {
    setAppliedSelected(tempSelected);
    onApply(tempSelected);
  };

  const handleChipDelete = (item) => {
    const next = appliedSelected.filter((x) => x !== item);
    setAppliedSelected(next);
    setTempSelected(next);
    onApply(next);
  };

  return (
    <Accordion
      sx={{
        boxShadow: "none",
        borderBottom: "1px solid #E6E6E6",
        my: 0,
        "&:before": { display: "none" },
        "&.Mui-expanded": { my: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          paddingTop: "18px",
          paddingBottom: "18px",
          alignItems: "flex-start",
          "&.Mui-expanded": { minHeight: "auto" },
          "& .MuiAccordionSummary-content": { margin: 0, alignItems: "flex-start", gap: 1 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography variant="fw400s14c000000" sx={{ lineHeight: "16px", color: "#1C1C1C", mr: 1 }}>
            {title}
          </Typography>
          {appliedSelected.length > 0 && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
              onClick={(e) => e.stopPropagation()}
            >
              {appliedSelected.map((item) => (
                <PrimaryChip
                  key={item}
                  label={item}
                  tooltipTitle={item}
                  tooltipPlacement="bottom"
                  onDelete={() => handleChipDelete(item)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Box>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
          />
          <Box sx={{ maxHeight: "200px", overflowY: "auto" }}>
            {filteredOptions.length === 0 ? (
              <Typography variant="fw400s12lh24c3D4B5A" sx={{ textAlign: "center", py: 2 }}>
                No options found
              </Typography>
            ) : (
              <List disablePadding>
                {filteredOptions.map((option) => (
                  <Box key={option} sx={{ py: 0.5 }}>
                    <ItemRowCheckBoxLabel
                      control={
                        <CustomCheckbox
                          checked={tempSelected.includes(option)}
                          onChange={() => handleSelection(option)}
                        />
                      }
                      label={
                        <Typography variant="fw400s12lh24c3D4B5A">{option}</Typography>
                      }
                    />
                  </Box>
                ))}
              </List>
            )}
          </Box>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5, mt: 1 }}
          >
            <Typography
              variant="fw500s12lh24c6D72F6"
              sx={{
                cursor: tempSelected.length > 0 ? "pointer" : "not-allowed",
                color: tempSelected.length > 0 ? "#6D72F6" : "#8F8F8F",
              }}
              onClick={() => tempSelected.length > 0 && handleClearSelection()}
            >
              Clear Selection
            </Typography>
            <PrimaryFormActionButton
              onClick={handleApply}
              sx={{ py: 0.75, px: 2, fontSize: "12px" }}
            >
              Apply
            </PrimaryFormActionButton>
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

DynamicFilterAccordion.propTypes = {
  title: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  searchPlaceholder: PropTypes.string,
  selectedValues: PropTypes.arrayOf(PropTypes.string).isRequired,
  onApply: PropTypes.func.isRequired,
};

function AlertSettingsRightSidebar({ isExpanded, onToggle, selectedFilters, onFilterChange }) {
  const [filterConfigs, setFilterConfigs] = useState([]);
  const [optionMap, setOptionMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  // Incrementing this key forces DynamicFilterAccordion instances to remount on Reset,
  // clearing their internal tempSelected/appliedSelected state.
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    let active = true;

    async function init() {
      try {
        const res = await getPagesMetaData("PlatformAlertsV2");
        if (!active) return;

        if (res?.responseStatus?.code === CODE.SUCCESS) {
          const filters = res.response?.response?.metaInfo?.alertSettings?.filters || [];
          setFilterConfigs(filters);

          const options = {};
          filters.filter((f) => f.type === "static").forEach((f) => {
            options[f.key] = (f.staticData || []).map((o) => o.value);
          });

          const dynamicResults = await Promise.all(
            filters
              .filter((f) => f.type === "dynamic")
              .map(async (filter) => {
                const result = await getFilterData({
                  type: "dynamic",
                  queryParams: filter.queryParams,
                });
                return {
                  key: filter.key,
                  data: (result?.response?.response?.filterData || []).map((o) => o.value),
                };
              })
          );

          if (!active) return;
          dynamicResults.forEach(({ key, data }) => { options[key] = data; });
          setOptionMap(options);
        }
      } catch {
        // non-critical; sidebar renders empty filters
      } finally {
        if (active) setIsLoading(false);
      }
    }

    init();
    return () => { active = false; };
  }, []);

  const handleReset = useCallback(
    (e) => {
      e.stopPropagation();
      filterConfigs.forEach((f) => onFilterChange(f.key, []));
      setResetKey((k) => k + 1);
    },
    [filterConfigs, onFilterChange]
  );

  return (
    <>
      {/* Toggle button — absolutely positioned inside the page-level position:relative wrapper */}
      <IconButton
        onClick={onToggle}
        data-testid="alert-settings-sidebar-toggle"
        sx={{
          position: "absolute",
          top: "16px",
          right: isExpanded ? "328px" : "8px",
          zIndex: 1000,
          backgroundColor: "transparent",
          width: "20px",
          height: "20px",
          transition: "right 0.3s ease-in-out",
          boxShadow: "none",
          "&:hover": { backgroundColor: "transparent" },
          "&:focus": { backgroundColor: "transparent" },
        }}
      >
        <PrimaryTooltip title={isExpanded ? "Collapse Panel" : "Expand Panel"} placement="bottom">
          <KeyboardTabIcon
            sx={{
              fontSize: 20,
              color: "#494949",
              transform: isExpanded ? "rotate(0deg)" : "rotate(180deg)",
              "&:hover": { color: "#6D72F6" },
            }}
          />
        </PrimaryTooltip>
      </IconButton>

      {/* Sidebar panel */}
      <Box
        sx={{
          width: isExpanded ? "320px" : "0px",
          transition: "width 0.3s ease-in-out",
          borderLeft: isExpanded ? "1px solid #E6E6E6" : "none",
          bgcolor: "#FFFFFF",
          height: "100%",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            width: "320px",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 0.3s ease-in-out",
            fontFamily: ThemeProperties.primaryFontV2,
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, flexShrink: 0 }}>
            <Box sx={{ mx: -3 }}>
              <Tabs
                value={0}
                sx={{
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    minHeight: "40px",
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8D8D8D",
                    p: 2,
                    "&.Mui-selected": { color: "#494949" },
                  },
                  "& .MuiTabs-indicator": { backgroundColor: "#494949" },
                }}
              >
                <Tab label="Settings" />
              </Tabs>
            </Box>
            <Divider sx={{ borderColor: "#E6E6E6", mt: 0, mx: -3 }} />
          </Box>

          {/* Scrollable content */}
          <Box
            sx={{
              flex: 1,
              overflowY: "scroll",
              pb: 3,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { bgcolor: "#D8D8E0", borderRadius: "999px" },
            }}
          >
            {/* Filters section */}
            <Accordion
              defaultExpanded
              sx={{
                boxShadow: "none",
                "&:before": { display: "none" },
                "&.Mui-expanded": { mt: 0 },
              }}
            >
              <AccordionSummary
                expandIcon={<KeyboardDoubleArrowDownIcon />}
                sx={{
                  pl: 3,
                  pr: 2,
                  "&.Mui-expanded": { minHeight: "auto" },
                  "& .MuiAccordionSummary-content": { margin: "20px 0" },
                }}
              >
                <Grid2
                  container
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: "100%", pr: 1 }}
                >
                  <Grid2 container alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "4px",
                        backgroundColor: "#EFF0FF",
                        p: 0.75,
                      }}
                    >
                      <FilterIconSvg sx={{ color: "#4D44D8" }} />
                    </Box>
                    <Typography variant="fw700s14c3D4B5A" sx={{ color: "#2E2E2E", lineHeight: "20px" }}>
                      Filters
                    </Typography>
                  </Grid2>
                  <Typography
                    data-testid="filters-reset-btn"
                    variant="fw500s12lh246D72F6"
                    onClick={handleReset}
                    sx={{ cursor: "pointer", "&:hover": { color: "#4D44D8" } }}
                  >
                    Reset
                  </Typography>
                </Grid2>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0, pb: 0 }}>
                {isLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                    <CartLoader />
                  </Box>
                ) : (
                  filterConfigs.map((filter) => (
                    <DynamicFilterAccordion
                      key={`${filter.key}-${resetKey}`}
                      title={filter.label}
                      options={optionMap[filter.key] || []}
                      searchPlaceholder={`Search ${filter.label}`}
                      selectedValues={selectedFilters[filter.key] || []}
                      onApply={(values) => onFilterChange(filter.key, values)}
                    />
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>
    </>
  );
}

AlertSettingsRightSidebar.propTypes = {
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedFilters: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

export default AlertSettingsRightSidebar;
